/**
 * Notion configuration access for the Settings modal (#40-#46).
 *
 * Same shape as `useScrapers`: one query key, and every mutation answers with the full #40 payload
 * so the cache is replaced rather than invalidated — the tab never flickers through a loading state
 * after saving a key or a mapping.
 */

import { useMutation, useQuery, useQueryClient, type UseQueryResult } from '@tanstack/react-query';
import type {
  NotionDatabaseUpdateBody,
  NotionDatabasesResponse,
  NotionMappingUpdateBody,
  NotionStateResponse,
} from '@lw/shared';
import {
  getNotion,
  getNotionDatabases,
  refreshNotionSchema,
  setNotionDatabase,
  setNotionMapping,
  setNotionToken,
  testNotion,
} from '../../api/notion';
import { QK } from '../../api/queryKeys';

export function useNotionQuery(enabled = true): UseQueryResult<NotionStateResponse, Error> {
  return useQuery({
    queryKey: QK.notion,
    queryFn: ({ signal }) => getNotion(signal),
    enabled,
    staleTime: 60_000,
  });
}

function useNotionMutation<TVars>(mutationFn: (vars: TVars) => Promise<NotionStateResponse>) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn,
    onSuccess: (state) => {
      queryClient.setQueryData(QK.notion, state);
    },
  });
}

export function useSetNotionToken() {
  return useNotionMutation((token: string) => setNotionToken(token));
}

export function useSetNotionDatabase() {
  return useNotionMutation((body: NotionDatabaseUpdateBody) => setNotionDatabase(body));
}

export function useRefreshNotionSchema() {
  return useNotionMutation((_vars: void) => refreshNotionSchema());
}

export function useSetNotionMapping() {
  return useNotionMutation((body: NotionMappingUpdateBody) => setNotionMapping(body));
}

export function useTestNotion() {
  return useMutation({ mutationFn: () => testNotion() });
}

/**
 * The live picker (#42). Disabled by default: it is a real Notion round trip, so it runs when the
 * user asks for the list and on `Refresh list`, never on every tab open.
 */
export function useNotionDatabasesQuery(
  enabled: boolean,
): UseQueryResult<NotionDatabasesResponse, Error> {
  return useQuery({
    queryKey: QK.notionDatabases,
    queryFn: ({ signal }) => getNotionDatabases(signal),
    enabled,
    staleTime: 60_000,
    retry: false,
  });
}
